<template>
  <div>
    <tx-dropdown ref="refSettingsDropdown" faicon="fa-light fa-gear" :tooltip="t('general.settings')" :max-height="150">
      <div class="w-50 h-[120px] flex flex-col overflow-hidden rounded">
        <div class="flex p-2 mb-2 bg-primary text-on-primary">
          <div class="flex-1 text-sm uppercase">
            {{ t('general.settings') }}
          </div>
          <font-awesome-icon class="w-4 h-4 cursor-pointer" icon="fa-light fa-xmark" @click="refSettingsDropdown?.closeDropdown" />
        </div>
        <div class="flex p-2 flex-col overflow-y-auto grow">
          <tx-switch v-model="form.showComments" type="inline" :label="t('general.showComments')" @change="onChange" />
          <tx-switch v-model="form.showFavorites" class="m-1" type="inline" :label="t('general.showFavorites')" @change="onChange" />
        </div>
      </div>
    </tx-dropdown>
  </div>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n'
import { onMounted, onUnmounted, reactive, ref, toRaw, watch } from 'vue'
import { useUserStore } from '@/store/userData'
import TxDropdown from '@/shared/components/TxDropdown.vue'
import TxSwitch from '@/shared/components/TxSwitch.vue'
import appConfig from '@/services/appConfig'
import { createContainer, getMyContainers, updateContainer } from '@/api/t1/container'
import utils from '@/services/utils'
import { containerTypeConstants } from '@/models/constants'

export interface ISettingItemWhiteboard {
  showComments: boolean
  showFavorites: boolean
}

const emit = defineEmits<{
  (e: 'change', form: ISettingItemWhiteboard): void
}>()

const { t } = useI18n()
const userStore = useUserStore()

const refSettingsDropdown = ref<InstanceType<typeof TxDropdown>>()

let isDirty = false
const form = reactive<ISettingItemWhiteboard>({
  showComments: true,
  showFavorites: true,
})

function onChange() {
  if (userStore.activeCatalog) {
    const settings: Record<number, ISettingItemWhiteboard> = {}
    settings[userStore.activeCatalog.CatalogCode] = toRaw(form)
    userStore.userPreferences.whiteboard.settings = settings
    appConfig.DB!.userPreferences.update(userStore.userPreferences.userId, { whiteboard: { settings } })
      .catch(e => console.error('Unable to update userPreferences', e))
  }
  notifyChange()
}

function notifyChange() {
  emit('change', form)
}

async function setModelDetailSettingsContainer(catalogCode: number, settings: ISettingItemWhiteboard) {
  const whiteboardSettingsContainerName = `${containerTypeConstants.whiteboardSettings}-${userStore.userProfile.Id}`
  const containersList = await utils.tryAsync(getMyContainers(catalogCode, containerTypeConstants.whiteboardSettings))
  if (containersList.success) {
    const container = containersList.result.data.find(f => f.ContainerName === whiteboardSettingsContainerName)
    if (!container) {
      await utils.tryAsync(createContainer(userStore.userProfile.Id, whiteboardSettingsContainerName, JSON.stringify(settings), catalogCode, containerTypeConstants.whiteboardSettings))
    }
    else {
      await utils.tryAsync(updateContainer(userStore.userProfile.Id, container.Id, whiteboardSettingsContainerName, JSON.stringify(settings), catalogCode))
    }
  }
}

onMounted(() => {
  if (userStore.activeCatalog && userStore.userPreferences?.whiteboard?.settings[userStore.activeCatalog.CatalogCode]) {
    form.showComments = userStore.userPreferences.whiteboard.settings[userStore.activeCatalog.CatalogCode].showComments
    form.showFavorites = userStore.userPreferences.whiteboard.settings[userStore.activeCatalog.CatalogCode].showFavorites
    notifyChange()
  }
})

onUnmounted(() => {
  if (userStore.activeCatalog && userStore.userPreferences?.whiteboard?.settings && isDirty) {
    setModelDetailSettingsContainer(userStore.activeCatalog.CatalogCode, userStore.userPreferences.whiteboard.settings[userStore.activeCatalog.CatalogCode])
  }
})

watch(() => userStore.userPreferences?.whiteboard?.settings, () => {
  isDirty = true
})
</script>
